import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, X, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';

export function EmailConfirmationBanner() {
  const { t } = useTranslation(['auth']);
  const { user, resendConfirmationEmail } = useAuth();
  const { toast } = useToast();

  const [isDismissed, setIsDismissed] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Don't show banner if user's email is confirmed or if dismissed
  if (!user || user.emailConfirmed || isDismissed) {
    return null;
  }

  const handleResend = async () => {
    if (!user?.email) return;

    setIsResending(true);
    try {
      await resendConfirmationEmail(user.email);

      toast({
        title: t('auth:emailConfirmation.emailSent'),
        description: t('auth:emailConfirmation.checkEmail'),
        variant: 'default',
      });
    } catch (error: any) {
      const isRateLimited = error.message?.includes('wait') || error.message?.includes('rate') || error.message?.includes('429');

      toast({
        title: isRateLimited
          ? t('auth:emailConfirmation.rateLimited')
          : t('auth:emailConfirmation.error'),
        description: isRateLimited
          ? t('auth:emailConfirmation.rateLimitedMessage')
          : (error.message || t('auth:emailConfirmation.failedToResend')),
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div
      role="alert"
      className="flex items-center gap-3 px-3 py-2 mb-6 rounded-lg border border-blue-200 bg-blue-50 text-blue-900"
    >
      <Mail className="h-4 w-4 text-blue-600 flex-shrink-0 hidden sm:block" />
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-2">
        <span className="text-sm font-medium">{t('auth:emailConfirmation.banner.title')}</span>
        <span className="text-sm text-blue-800 truncate">
          {t('auth:emailConfirmation.banner.message')}
        </span>
      </div>
      <Button
        onClick={handleResend}
        disabled={isResending}
        size="sm"
        variant="outline"
        className="border-blue-300 text-blue-700 hover:bg-blue-100 flex-shrink-0 whitespace-nowrap"
      >
        {isResending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('auth:emailConfirmation.banner.sending')}
          </>
        ) : (
          <>
            <CheckCircle className="mr-2 h-4 w-4" />
            {t('auth:emailConfirmation.banner.resendButton')}
          </>
        )}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 hover:bg-blue-100 flex-shrink-0"
        onClick={() => setIsDismissed(true)}
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
